import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PaidDuty from "@/pages/PaidDuty";
import Overtime from "@/pages/Overtime";
import RegularHours from "@/pages/RegularHours";
import CourtTime from "@/pages/CourtTime";
import CalendarPage from "@/pages/CalendarPage";
import SchedulePage from "@/pages/SchedulePage";
import Reports from "@/pages/Reports";
import Analytics from "@/pages/Analytics";
import Billing from "@/pages/Billing";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminPlatform from "@/pages/admin/AdminPlatform";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminContent from "@/pages/admin/AdminContent";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminLeave from "@/pages/admin/AdminLeave";
import AdminTax from "@/pages/admin/AdminTax";
import ForgotPassword from "@/pages/ForgotPassword";
import FinanceCentre from "@/pages/finance/FinanceCentre";
import MortgagePage from "@/pages/finance/MortgagePage";
import VehiclePage from "@/pages/finance/VehiclePage";
import DebtPage from "@/pages/finance/DebtPage";
import BudgetPage from "@/pages/finance/BudgetPage";
import SavingsPage from "@/pages/finance/SavingsPage";
import NetWorthPage from "@/pages/finance/NetWorthPage";
import TaxCentre from "@/pages/tax/TaxCentre";
import TaxReturn from "@/pages/tax/TaxReturn";
import PreparerDashboard from "@/pages/tax/PreparerDashboard";
import PreparerCase from "@/pages/tax/PreparerCase";
import Paycheques from "@/pages/Paycheques";
import PaychequeDetail from "@/pages/PaychequeDetail";
import Settings from "@/pages/Settings";
import Deductions from "@/pages/Deductions";
import Projections from "@/pages/Projections";
import RetirementPlanner from "@/pages/RetirementPlanner";
import GoalPlanner from "@/pages/GoalPlanner";
import RetirementReport from "@/pages/RetirementReport";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import PayChanges from "@/pages/PayChanges";
import PayTypes from "@/pages/PayTypes";
import UpgradeModal from "@/components/UpgradeModal";
import Landing from "@/pages/Landing";
import Marketplace from "@/pages/Marketplace";
import OrgDirectory from "@/pages/OrgDirectory";
import OrgPage from "@/pages/OrgPage";

function Protected({ children }) {
  const { user } = useAuth();
  const { data: cfg } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/public-config")).data, staleTime: 60000 });
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="app-loading">
        Loading…
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (cfg?.maintenance_mode && user.role !== "admin")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="maintenance-screen">
        <div className="h-14 w-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-5">
          <span className="text-2xl">🛠️</span>
        </div>
        <h1 className="text-2xl font-black tracking-tight">Under maintenance</h1>
        <p className="text-muted-foreground mt-2 max-w-md">{cfg.maintenance_message}</p>
      </div>
    );
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function LandingRoute() {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Login defaultMode="register" /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/" element={<LandingRoute />} />
            <Route path="/r/:token" element={<RetirementReport />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
            <Route path="/organizations" element={<Protected><OrgDirectory /></Protected>} />
            <Route path="/organizations/:id" element={<Protected><OrgPage /></Protected>} />
            <Route path="/paid-duty" element={<Protected><PaidDuty /></Protected>} />
            <Route path="/overtime" element={<Protected><Overtime /></Protected>} />
            <Route path="/regular-hours" element={<Protected><RegularHours /></Protected>} />
            <Route path="/deductions" element={<Protected><Deductions /></Protected>} />
            <Route path="/projections" element={<Protected><Projections /></Protected>} />
            <Route path="/retirement" element={<Protected><RetirementPlanner /></Protected>} />
            <Route path="/goals" element={<Protected><GoalPlanner /></Protected>} />
            <Route path="/pay-changes" element={<Protected><PayChanges /></Protected>} />
            <Route path="/pay-types" element={<Protected><PayTypes /></Protected>} />
            <Route path="/court" element={<Protected><CourtTime /></Protected>} />
            <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/finance" element={<Protected><FinanceCentre /></Protected>} />
            <Route path="/finance/mortgage" element={<Protected><MortgagePage /></Protected>} />
            <Route path="/finance/vehicle" element={<Protected><VehiclePage /></Protected>} />
            <Route path="/finance/debts" element={<Protected><DebtPage /></Protected>} />
            <Route path="/finance/budget" element={<Protected><BudgetPage /></Protected>} />
            <Route path="/finance/savings" element={<Protected><SavingsPage /></Protected>} />
            <Route path="/finance/net-worth" element={<Protected><NetWorthPage /></Protected>} />
            <Route path="/tax" element={<Protected><TaxCentre /></Protected>} />
            <Route path="/tax/:id" element={<Protected><TaxReturn /></Protected>} />
            <Route path="/preparer" element={<Protected><PreparerDashboard /></Protected>} />
            <Route path="/preparer/:id" element={<Protected><PreparerCase /></Protected>} />
            <Route path="/schedule" element={<Protected><SchedulePage /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
            <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
            <Route path="/settings/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="platform" element={<AdminPlatform />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="leave" element={<AdminLeave />} />
              <Route path="tax" element={<AdminTax />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>
            <Route path="/paycheques" element={<Protected><Paycheques /></Protected>} />
            <Route path="/paycheques/:id" element={<Protected><PaychequeDetail /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <UpgradeModal />
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
